import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Minus, X, ShoppingBag, ArrowUpRight, Eye } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { SECTIONS, SHOP } from "@/constants/testIds";
import { ASSETS } from "@/lib/assets";

const BOTTLE_FALLBACKS = {
  "nocna-tajna": ASSETS.heroBottle,
};

const formatRSD = (n) => new Intl.NumberFormat("sr-RS").format(n);

const gridParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const cardItem = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Shop() {
  const { t, lang } = useI18n();
  const {
    wines,
    cart,
    items,
    total,
    cartCount,
    drawerOpen,
    setDrawerOpen,
    checkoutOpen,
    setCheckoutOpen,
    quickView,
    setQuickView,
    add,
    inc,
    dec,
    remove,
    placeOrder,
  } = useCart();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    note: "",
    payment_method: "cash",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!form.name || !form.phone || !form.email || !form.address || !form.city) return;
    setLoading(true);
    const ok = await placeOrder(form, t);
    if (ok) setForm({ name: "", email: "", phone: "", address: "", city: "", note: "", payment_method: "cash" });
    setLoading(false);
  };

  return (
    <section
      id="shop"
      data-testid={SECTIONS.shop}
      className="relative bg-[#070707] text-white py-28 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {/* Header + open cart button */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#c5a572]" />
              <span className="text-[11px] tracking-[0.42em] text-[#c5a572] uppercase">
                {t.shop.eyebrow}
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight">
              {t.shop.title}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <p className="text-white/60 leading-relaxed font-light max-w-md">{t.shop.body}</p>
            <button
              data-testid="open-cart-button"
              onClick={() => setDrawerOpen(true)}
              className="self-start inline-flex items-center gap-2 border border-white/15 hover:border-[#c5a572] text-white/85 hover:text-[#c5a572] px-5 py-3 rounded-full text-[11px] tracking-[0.28em] uppercase transition-colors duration-300"
            >
              <ShoppingBag size={16} />
              {t.shop.cart}
              <span className="ml-1 w-6 h-6 rounded-full bg-[#c5a572] text-[#0a0a0a] flex items-center justify-center text-[11px] font-semibold">
                {cartCount}
              </span>
            </button>
          </div>
        </div>

        {/* Animated wine grid */}
        <motion.div
          data-testid={SHOP.grid}
          variants={gridParent}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {wines.map((w) => {
            const img = w.image || BOTTLE_FALLBACKS[w.slug] || ASSETS.heroBottle;
            const inCart = cart[w.slug] || 0;
            return (
              <motion.article
                key={w.id}
                variants={cardItem}
                data-testid={SHOP.card(w.slug)}
                className="group relative bg-[#0d0d0d] border border-white/10 hover:border-[#c5a572]/40 transition-colors duration-500 flex flex-col overflow-hidden"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#080808]">
                  <img
                    src={img}
                    alt={w.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                  />
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  {w.featured && (
                    <span className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.32em] uppercase bg-[#c5a572] text-[#0a0a0a] px-2 py-1">
                      Signature
                    </span>
                  )}
                  {/* Quick View button */}
                  <button
                    data-testid={`quick-view-${w.slug}`}
                    onClick={() => setQuickView(w)}
                    className="absolute left-1/2 bottom-5 -translate-x-1/2 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm hover:bg-white text-[#0a0a0a] px-5 py-2.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-medium"
                  >
                    <Eye size={13} /> {lang === "sr" ? "Brzi pregled" : "Quick View"}
                  </button>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-white/45">
                      {w.type}
                    </div>
                    {w.vintage && (
                      <div className="font-mono text-[11px] tracking-[0.2em] text-[#c5a572]">
                        {w.vintage}
                      </div>
                    )}
                  </div>
                  <h3 className="mt-2 font-serif text-2xl">{w.name}</h3>
                  <p className="mt-3 text-white/60 text-sm font-light line-clamp-2">
                    {lang === "sr" ? w.description_sr : w.description_en}
                  </p>
                  <div className="mt-auto pt-6 flex items-end justify-between">
                    <div>
                      <div className="font-serif text-2xl text-[#c5a572]">
                        {formatRSD(w.price_rsd)} <span className="text-sm">{t.shop.rsd}</span>
                      </div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1">
                        {t.shop.perBottle}
                      </div>
                    </div>
                    <button
                      data-testid={SHOP.add(w.slug)}
                      onClick={() => add(w.slug)}
                      className="inline-flex items-center gap-2 bg-[#c5a572] hover:bg-[#d4b685] text-[#0a0a0a] px-4 py-2.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-medium transition-colors"
                    >
                      {inCart > 0 ? `+${inCart}` : <Plus size={14} />}
                      {t.shop.add}
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>

      {/* Floating cart button (always visible when cart has items) */}
      {cartCount > 0 && !drawerOpen && (
        <motion.button
          data-testid="floating-cart-button"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setDrawerOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-3 bg-[#c5a572] hover:bg-[#d4b685] text-[#0a0a0a] pl-5 pr-3 py-3 rounded-full shadow-[0_20px_50px_-10px_rgba(0,0,0,0.6)] text-[11px] tracking-[0.28em] uppercase font-medium"
        >
          <ShoppingBag size={16} />
          <span>{t.shop.cart}</span>
          <span className="w-7 h-7 rounded-full bg-[#0a0a0a] text-[#c5a572] flex items-center justify-center text-[11px] font-bold">
            {cartCount}
          </span>
        </motion.button>
      )}

      {/* Cart drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="right"
          data-testid={SHOP.cart}
          overlayClassName="bg-black/50 backdrop-blur-md"
          className="bg-[#0a0a0a] border-l border-white/10 text-white sm:max-w-md w-full p-0 flex flex-col"
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/5">
            <SheetTitle className="text-white font-serif text-2xl tracking-tight flex items-center gap-3">
              <ShoppingBag size={18} className="text-[#c5a572]" />
              {t.shop.cart}
              <span className="ml-auto text-[10px] tracking-[0.32em] text-white/45 uppercase font-sans font-normal">
                {cartCount} {cartCount === 1 ? "vino" : "vina"}
              </span>
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            {items.length === 0 ? (
              <div className="text-center py-16 text-white/50 font-light text-sm">
                {t.shop.emptyCart}
              </div>
            ) : (
              <ul className="space-y-5">
                {items.map((it) => (
                  <li
                    key={it.slug}
                    data-testid={SHOP.cartItem(it.slug)}
                    className="flex items-start gap-4 pb-5 border-b border-white/5 last:border-0"
                  >
                    <div className="w-16 h-20 flex-shrink-0 overflow-hidden bg-[#0d0d0d] border border-white/5">
                      {it.image && (
                        <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg leading-tight truncate">{it.name}</div>
                      <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mt-1">
                        {it.type}
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center border border-white/10">
                          <button
                            data-testid={SHOP.cartDec(it.slug)}
                            onClick={() => dec(it.slug)}
                            className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white"
                            aria-label="dec"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="w-8 text-center text-sm">{it.qty}</span>
                          <button
                            data-testid={SHOP.cartInc(it.slug)}
                            onClick={() => inc(it.slug)}
                            className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white"
                            aria-label="inc"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <div className="text-[#c5a572] font-serif text-base">
                          {formatRSD(it.price_rsd * it.qty)} {t.shop.rsd}
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={SHOP.cartRemove(it.slug)}
                      onClick={() => remove(it.slug)}
                      className="text-white/30 hover:text-white"
                      aria-label="remove"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <SheetFooter className="px-6 py-6 border-t border-white/5 flex-col sm:flex-col gap-4 bg-[#0a0a0a]">
            <div className="flex items-end justify-between w-full">
              <div className="text-[10px] tracking-[0.32em] uppercase text-white/45">
                {t.shop.total}
              </div>
              <div data-testid={SHOP.cartTotal} className="font-serif text-2xl text-[#c5a572]">
                {formatRSD(total)} {t.shop.rsd}
              </div>
            </div>
            <button
              data-testid={SHOP.checkoutOpen}
              disabled={items.length === 0}
              onClick={() => setCheckoutOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#c5a572] hover:bg-[#d4b685] disabled:opacity-40 disabled:cursor-not-allowed text-[#0a0a0a] px-6 py-3.5 rounded-full text-[11px] tracking-[0.28em] uppercase font-medium transition-colors"
            >
              {t.shop.checkout}
              <ArrowUpRight size={14} />
            </button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Checkout dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent
          data-testid={SHOP.checkoutForm}
          className="bg-[#0a0a0a] border border-white/10 text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-white">
              {t.shop.checkout}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="mt-4 space-y-4">
            <div className="grid sm:grid-cols-2 gap-5">
              <CField
                testid={SHOP.field("name")}
                label={t.shop.name}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <CField
                testid={SHOP.field("phone")}
                label={t.shop.phone}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />
              <CField
                testid={SHOP.field("email")}
                type="email"
                label={t.shop.email}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <CField
                testid={SHOP.field("city")}
                label={t.shop.city}
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                required
              />
              <div className="sm:col-span-2">
                <CField
                  testid={SHOP.field("address")}
                  label={t.shop.address}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[10px] tracking-[0.32em] uppercase text-white/50 mb-2">
                  {t.shop.note}
                </label>
                <textarea
                  data-testid={SHOP.field("note")}
                  rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#c5a572] outline-none py-3 text-white font-light transition-colors"
                />
              </div>
              <div className="sm:col-span-2 pt-2">
                <div className="text-[10px] tracking-[0.32em] uppercase text-white/50 mb-4">
                  {t.shop.paymentTitle}
                </div>
                <div className="grid sm:grid-cols-3 gap-3" data-testid="payment-methods">
                  {[
                    { id: "cash", label: t.shop.paymentCash, desc: t.shop.paymentCashDesc, icon: "₿" },
                    { id: "card", label: t.shop.paymentCard, desc: t.shop.paymentCardDesc, icon: "▭" },
                    { id: "ips", label: t.shop.paymentIps, desc: t.shop.paymentIpsDesc, icon: "▤" },
                  ].map((m) => {
                    const active = form.payment_method === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`payment-${m.id}`}
                        onClick={() => setForm({ ...form, payment_method: m.id })}
                        className={`relative text-left p-4 rounded-md border transition-all duration-300 ${
                          active
                            ? "border-[#c5a572] bg-[#c5a572]/10"
                            : "border-white/10 hover:border-white/25 bg-transparent"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="font-serif text-lg leading-none">{m.label}</div>
                          <span
                            className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                              active ? "border-[#c5a572]" : "border-white/30"
                            }`}
                          >
                            {active && <span className="w-2 h-2 rounded-full bg-[#c5a572]" />}
                          </span>
                        </div>
                        <div className="mt-2 text-[11px] text-white/55 font-light leading-snug">
                          {m.desc}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-white/10 flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-white/45">
                  {t.shop.total}
                </div>
                <div className="font-serif text-2xl text-[#c5a572] mt-1">
                  {formatRSD(total)} {t.shop.rsd}
                </div>
              </div>
              <button
                data-testid={SHOP.submit}
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-[#c5a572] hover:bg-[#d4b685] disabled:opacity-60 text-[#0a0a0a] px-7 py-4 rounded-full text-[12px] tracking-[0.28em] uppercase font-medium transition-colors"
              >
                {loading ? t.shop.placing : t.shop.checkout}
                <ArrowUpRight size={14} />
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick View dialog */}
      <Dialog open={!!quickView} onOpenChange={(o) => !o && setQuickView(null)}>
        <DialogContent
          data-testid="quick-view-dialog"
          className="bg-[#0a0a0a] border border-white/10 text-white sm:max-w-3xl p-0 overflow-hidden"
        >
          {quickView && (
            <div className="grid md:grid-cols-2">
              <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[460px] bg-[#080808]">
                <img
                  src={quickView.image || BOTTLE_FALLBACKS[quickView.slug] || ASSETS.heroBottle}
                  alt={quickView.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="p-8 lg:p-10 flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.32em] uppercase text-white/45">
                    {quickView.type}
                  </span>
                  {quickView.vintage && (
                    <span className="font-mono text-[11px] tracking-[0.2em] text-[#c5a572]">
                      {quickView.vintage}
                    </span>
                  )}
                </div>
                <DialogTitle className="mt-4 font-serif text-4xl text-white tracking-tight">
                  {quickView.name}
                </DialogTitle>
                <p className="mt-5 text-white/65 leading-relaxed font-light">
                  {lang === "sr" ? quickView.description_sr : quickView.description_en}
                </p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-white/45 mb-2">
                    {lang === "sr" ? "Note" : "Notes"}
                  </div>
                  <div className="font-serif italic text-white/85">
                    {lang === "sr" ? quickView.notes_sr : quickView.notes_en}
                  </div>
                </div>
                <div className="mt-auto pt-8 flex items-end justify-between">
                  <div>
                    <div className="font-serif text-3xl text-[#c5a572]">
                      {formatRSD(quickView.price_rsd)} <span className="text-base">{t.shop.rsd}</span>
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1">
                      {t.shop.perBottle}
                    </div>
                  </div>
                  <button
                    data-testid={`qv-add-${quickView.slug}`}
                    onClick={() => {
                      add(quickView.slug);
                      setQuickView(null);
                    }}
                    className="inline-flex items-center gap-2 bg-[#c5a572] hover:bg-[#d4b685] text-[#0a0a0a] px-6 py-3 rounded-full text-[11px] tracking-[0.28em] uppercase font-medium transition-colors"
                  >
                    <Plus size={14} />
                    {t.shop.add}
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function CField({ testid, label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.32em] uppercase text-white/50 mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#c5a572] outline-none py-3 text-white font-light transition-colors"
      />
    </div>
  );
}
